import React, { useState, useEffect } from 'react';
import { Briefcase, MapPin, Clock, Search, Filter, ChevronRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import jobService, { PublicJob, JobFilters } from '../../services/user/jobService';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const Careers: React.FC = () => {
  const [jobs, setJobs] = useState<PublicJob[]>([]);
  const [filters, setFilters] = useState<JobFilters>({ locations: [], types: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const [heroRef, heroVisible] = useScrollAnimation();

  useEffect(() => {
    fetchFilters();
    fetchJobs();
  }, []);

  const fetchFilters = async () => {
    try {
      const data = await jobService.getJobFilters();
      setFilters(data);
    } catch (err) {
      console.error('Failed to fetch filters', err);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = await jobService.getAllJobs({
        location: selectedLocation,
        type: selectedType,
        department: selectedDepartment,
        search: search
      });
      setJobs(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchJobs();
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-white border-b border-neutral-100">
        <div className={`max-w-7xl mx-auto text-center transition-all duration-700 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
          <h1 className="text-5xl md:text-6xl font-bold text-neutral-900 mb-6 tracking-tight">
            Join Our <span className="text-primary-600">Mission</span>
          </h1>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
            Help us build the future of digital commerce and business discovery.
            Work with a team that values innovation, integrity, and impact.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-12 px-4">
        {/* Filters and Search */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-neutral-100 mb-12">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
              <input
                type="text"
                placeholder="Search jobs..."
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none text-neutral-700"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
              <select
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none appearance-none text-neutral-700"
                value={selectedLocation}
                onChange={(e) => { setSelectedLocation(e.target.value); }}
              >
                <option value="all">All Locations</option>
                {filters.locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>

            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
              <select
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none appearance-none text-neutral-700"
                value={selectedType}
                onChange={(e) => { setSelectedType(e.target.value); }}
              >
                <option value="all">All Types</option>
                {filters.types.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>

            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-md hover:shadow-lg active:scale-95"
            >
              Search Jobs
            </button>
          </form>

          <div className="flex flex-wrap gap-2 mt-4">
            <span className="text-sm font-medium text-neutral-500 mr-2 flex items-center">
              <Filter size={16} className="mr-1" /> Departments:
            </span>
            <button
              onClick={() => setSelectedDepartment('all')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${selectedDepartment === 'all'
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-neutral-100 text-neutral-600 border border-neutral-200 hover:bg-neutral-200'
                }`}
            >
              All
            </button>
            {filters.departments.map(dept => (
              <button
                key={dept}
                onClick={() => setSelectedDepartment(dept)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${selectedDepartment === dept
                  ? 'bg-primary-100 text-primary-700 border border-primary-200'
                  : 'bg-neutral-100 text-neutral-600 border border-neutral-200 hover:bg-neutral-200'
                  }`}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>

        {/* Job Listings */}
        <div className="space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-neutral-100 shadow-sm">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
              <p className="text-neutral-500 font-medium">Fetching available opportunities...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-neutral-100 shadow-sm">
              <AlertCircle size={48} className="text-red-500 mb-4" />
              <p className="text-neutral-900 font-bold text-xl">{error}</p>
              <button
                onClick={fetchJobs}
                className="mt-4 text-primary-600 font-semibold hover:underline"
              >
                Try again
              </button>
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-neutral-100 shadow-sm">
              <Briefcase size={64} className="text-neutral-200 mb-4" />
              <h3 className="text-2xl font-bold text-neutral-900 mb-2">No jobs found</h3>
              <p className="text-neutral-500">Try adjusting your filters or search terms.</p>
              <button
                onClick={() => {
                  setSearch('');
                  setSelectedLocation('all');
                  setSelectedType('all');
                  setSelectedDepartment('all');
                  fetchJobs();
                }}
                className="mt-6 bg-primary-50 text-primary-700 px-6 py-2 rounded-xl font-bold hover:bg-primary-100 transition-all border border-primary-100"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            jobs.map((job) => (
              <div
                key={job.id}
                className="group bg-white p-8 rounded-2xl border border-neutral-100 shadow-sm hover:shadow-xl hover:border-primary-100 transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-1.5 h-full bg-primary-500 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-bold rounded-full border border-primary-100 uppercase tracking-wider">
                        {job.department}
                      </span>
                      <span className="text-neutral-400 text-xs font-medium">
                        Posted {new Date(job.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    <h3 className="text-2xl font-bold text-neutral-900 group-hover:text-primary-600 transition-colors">
                      {job.title}
                    </h3>

                    <div className="flex flex-wrap items-center gap-6 text-neutral-500 text-sm font-medium">
                      <div className="flex items-center gap-1.5">
                        <MapPin size={18} className="text-primary-500/70" />
                        {job.location}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock size={18} className="text-primary-500/70" />
                        {job.employment_type}
                      </div>
                      {job.salary && (
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-lg border border-green-100">
                          {job.salary}
                        </div>
                      )}
                    </div>

                    <p className="text-neutral-600 line-clamp-2 max-w-3xl pt-2">
                      {job.short_description}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <Link
                      to={`/careers/${job.id}`}
                      className="inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-primary-600 text-white font-bold py-3 px-8 rounded-xl transition-all shadow-md hover:shadow-lg group-hover:scale-105 active:scale-95 whitespace-nowrap"
                    >
                      View Details
                      <ChevronRight size={20} />
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary-600 text-white overflow-hidden relative">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-primary-700 rounded-full opacity-30 blur-3xl"></div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">Don't see a perfect fit?</h2>
          <p className="text-xl text-primary-100 mb-10 max-w-2xl mx-auto">
            We're always looking for talented individuals to join our growing team.
            Send us your resume and we'll keep you in mind for future openings.
          </p>
          <Link
            to="/app"
            className="inline-block bg-white text-primary-600 hover:bg-neutral-100 font-bold py-4 px-10 rounded-2xl transition-all shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
          >
            General Application
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;
